import {CutOutPreview, minMaxPositionSnapDistance} from "./cutOutPreview";
import {placeableWalls} from "./cutOutGeneration";
import {Box3, Vector3} from "three";
import {findChildByName, findChildrenByName} from "../../utils/threeUtils";
import {toFixedFloat} from "../../utils/utils";
import {areOverlapping} from "../../utils/mathUtils";
import * as interiorDoorConstants from "../../constants/cutOuts/interiorDoorConstants";
import {getIntersectingWalls} from "../walls/wall";
import {mousePosition} from "../../main";
import * as Globals from "../../globals";
import {controlObjectName} from "../../constants/nameConstants";

export class InteriorDoorPreview extends CutOutPreview {
    constructor(width, height, bottomOffset) {
        super(width, height, bottomOffset);
        this.marginLeftRight = interiorDoorConstants.startEndMargin;
        this.snapToHBEElements = false;
        this.placeableWalls = placeableWalls.interior;
        this.controlRange = null;
        this.wallOccupiedRanges = [];
    }

    static createFromControlObject(controlObject) {
        let width = controlObject.userData.width;
        let height = controlObject.userData.height;
        let bottomOffset = controlObject.userData.bottomOffset;

        let preview = new InteriorDoorPreview(width, height, bottomOffset);
        preview.object = controlObject;
        preview.recreated = true;
        return preview;
    }

    checkValidPosition(wallGroup, wallOrientation) {
        //Limit cutout placement to allowed wall type.
        //Zuerst ausführen. Keine lange Berechnung notwendig, wenn sowieso nicht erlaubt.
        let isOuterWall = wallGroup.userData.isOuterWall;
        if (this.placeableWalls === placeableWalls.interior && isOuterWall) {
            return false;
        } else if (this.placeableWalls === placeableWalls.exterior && !isOuterWall) {
            return false;
        }

        //Create BoundingBox for wall
        let wallBounds = new Box3().setFromObject(wallGroup);
        let wallStart = wallOrientation === "x" ? wallBounds.min.x : wallBounds.min.z;
        let wallEnd = wallOrientation === "x" ? wallBounds.max.x : wallBounds.max.z;

        let newControlBounds = new Box3().setFromObject(this.object);
        let newControlStart = wallOrientation === "x" ? newControlBounds.min.x : newControlBounds.min.z;
        let newControlEnd = wallOrientation === "x" ? newControlBounds.max.x : newControlBounds.max.z;

        let firstElement = null;
        let lastElement = null;

        for (let i = 0; i < wallGroup.children.length; i++) {
            let child = wallGroup.children[i];
            if (child.type === "Mesh") {
                if (child.userData.isFirstElement) {
                    firstElement = child;
                } else if (child.userData.isLastElement) {
                    lastElement = child;
                }
            }
        }

        if (firstElement === null || lastElement === null) {
            console.error("Wall doesnt contain a first and last element.");
        }

        let firstElementBounds = new Box3().setFromObject(findChildByName(firstElement, "wallMeshBBox", true));
        let lastElementBounds = new Box3().setFromObject(findChildByName(lastElement, "wallMeshBBox", true));
        let firstElementSize = toFixedFloat(wallOrientation === "x" ? firstElementBounds.getSize(new Vector3()).x : firstElementBounds.getSize(new Vector3()).z);
        let lastElementSize = toFixedFloat(wallOrientation === "x" ? lastElementBounds.getSize(new Vector3()).x : lastElementBounds.getSize(new Vector3()).z);


        //Ggf. erstes und letztes Element swappen um sicherzustellen dass der start immer an der kleineren Position liegt
        if ((wallOrientation === "x" && firstElementBounds.min.x > lastElementBounds.min.x) || (wallOrientation === "z" && firstElementBounds.min.z > lastElementBounds.min.z)) {
            let tmp = firstElementSize;
            firstElementSize = lastElementSize;
            lastElementSize = tmp;
        }

        // let firstElementMargin = firstElementSize >= this.marginLeftRight ? this.marginLeftRight : firstElementSize + this.marginLeftRight;
        // let lastElementMargin = lastElementSize >= this.marginLeftRight ? this.marginLeftRight : lastElementSize + this.marginLeftRight;
        //Update 19.07.23: Die Breite des existierenden Elements ist nun irrelevant. Der Mindestabstand muss einfach eingehalten werden.
        // Wandelemente werden anschließend eh angepasst und auf das Mindestmaß gebracht
        let firstElementMargin = this.marginLeftRight;
        let lastElementMargin = this.marginLeftRight;

        //Check if margin to wall end is valid
        /* Bei Platzierung am letzten Element muss dieses mindestens 15cm sein.
         * Wenn mind 15cm kann am zweiten oder vorletzten bereits platziert werden
         * Wenn kleiner 15cm muss ein zusätzliches Element zwischen liegen also ab 3. Elemente (von links oder rechts)
         * */

        if (!(toFixedFloat(newControlStart - firstElementMargin) >= toFixedFloat(wallStart) &&
            toFixedFloat(newControlEnd + lastElementMargin) <= toFixedFloat(wallEnd))) {
            return false;
        }

        //Check positioning of control object against existing controlObjects
        let controlObjects = findChildrenByName(wallGroup, controlObjectName, false, true);
        for (let i = 0; i < controlObjects.length; i++) {
            let controlObject = controlObjects[i];
            let controlObjectMargin = controlObject.userData.marginLeftRight; //TODO Eventuell in Float parsen
            let biggestMargin = controlObjectMargin > this.marginLeftRight ? controlObjectMargin : this.marginLeftRight; //Margin zwischen Elementen die eingehalten werden muss

            let controlBounds = new Box3().setFromObject(controlObject);
            let controlStart = wallOrientation === "x" ? controlBounds.min.x - biggestMargin : controlBounds.min.z - biggestMargin;
            let controlEnd = wallOrientation === "x" ? controlBounds.max.x + biggestMargin : controlBounds.max.z + biggestMargin;

            let overlap = areOverlapping({start: controlStart, finish: controlEnd}, {
                start: newControlStart,
                finish: newControlEnd
            });
            if (overlap) {
                //If any element gets overlapped > controlObject not placeable
                return false;
            }
        }

        //Check if door is too close to an existing inner wall
        let intersectingWalls = getIntersectingWalls(wallGroup, this.innerWalls, false);
        this.wallOccupiedRanges = [];
        for (let i = 0; i < intersectingWalls.length; i++) {
            let intersectingWall = intersectingWalls[i];
            let intersectingWallBounds = new Box3().setFromObject(intersectingWall);
            if (wallOrientation === "x") {
                this.wallOccupiedRanges.push({
                    start: intersectingWallBounds.min.x,
                    finish: intersectingWallBounds.max.x
                });
            } else {
                this.wallOccupiedRanges.push({
                    start: intersectingWallBounds.min.z,
                    finish: intersectingWallBounds.max.z
                });
            }
        }

        let controlBounds = new Box3().setFromObject(this.object);
        this.controlRange = null;
        if (wallOrientation === "x") {
            this.controlRange = {
                start: controlBounds.min.x - this.marginLeftRight,
                finish: controlBounds.max.x + this.marginLeftRight
            };
        } else {
            this.controlRange = {
                start: controlBounds.min.z - this.marginLeftRight,
                finish: controlBounds.max.z + this.marginLeftRight
            };
        }

        for (let i = 0; i < this.wallOccupiedRanges.length; i++) {
            if (areOverlapping(this.controlRange, this.wallOccupiedRanges[i])) {
                return false;
            }
        }

        if (this.checkForSupportBeamOverlap(wallOrientation)) {
            return false;
        }

        return true;
    }

    setPositionOfControlObject(wallGroup, wallMeshBBox, wallOrientation) {
        //Positionierung des Previewobjekts
        let originalYPosition = this.object.position.y;
        this.object.position.set(0, 0, 0); //Nullen der Position da diese sonst auf Matrix addiert wird
        this.object.rotation.set(-Math.PI / 2, 0, wallOrientation === "z" ? Math.PI / 2 : 0);

        let wallMeshBBoxGlobalPosition = wallMeshBBox.getWorldPosition(new Vector3());
        this.object.position.copy(wallMeshBBoxGlobalPosition);
        if (this.snapToHBEElements) {
            if (this.elementWidth % 2 === 0) {
                if (wallOrientation === "x") {
                    let offset = mousePosition.x > wallMeshBBoxGlobalPosition.x ? Globals.HBESectionWidth / 2 : -Globals.HBESectionWidth / 2;
                    this.object.position.x += offset;
                } else {
                    let offset = mousePosition.z > wallMeshBBoxGlobalPosition.z ? Globals.HBESectionWidth / 2 : -Globals.HBESectionWidth / 2;
                    this.object.position.z += offset;
                }
            }
        } else {
            let bounds = new Box3().setFromObject(wallGroup);
            let result = false;
            if (wallOrientation === "x") {
                this.object.position.x = mousePosition.x;
                let minSnapPoint = toFixedFloat(bounds.min.x + this.marginLeftRight + (this.width / 2));
                let maxSnapPoint = toFixedFloat(bounds.max.x - this.marginLeftRight - (this.width / 2));
                result = this.trySnappingToWallEnd(wallOrientation, {start: minSnapPoint, finish: maxSnapPoint});
            } else {
                this.object.position.z = mousePosition.z;
                let minSnapPoint = bounds.min.z + this.marginLeftRight + (this.width / 2);
                let maxSnapPoint = bounds.max.z - this.marginLeftRight - (this.width / 2);
                result = this.trySnappingToWallEnd(wallOrientation, {start: minSnapPoint, finish: maxSnapPoint});
            }

            //Check result to see if element was snapped already. > Only snap once
            if (!result) {
                if (this.wallOccupiedRanges.length > 0) {
                    for (let i = 0; i < this.wallOccupiedRanges.length; i++) {
                        if (wallOrientation === "x") {
                            this.object.position.x = mousePosition.x;
                        } else {
                            this.object.position.z = mousePosition.z;
                        }
                        let range = this.wallOccupiedRanges[i];
                        let invertedRange = {
                            start: range.start - this.marginLeftRight - (this.width / 2),
                            finish: range.finish + this.marginLeftRight + (this.width / 2)
                        };
                        result = this.trySnapToInnerWall(wallOrientation, invertedRange);
                        if (result) {
                            break;
                        }
                    }
                }
            }
        }

        this.object.position.setY(originalYPosition);
    }

    trySnappingToWallEnd(wallOrientation, range) {
        let minSnapPoint = toFixedFloat(range.start);
        let maxSnapPoint = toFixedFloat(range.finish);

        if (wallOrientation === "x") {
            if (this.object.position.x <= minSnapPoint + minMaxPositionSnapDistance) {
                this.object.position.x = minSnapPoint;
                return true;
            }
            if (this.object.position.x >= maxSnapPoint - minMaxPositionSnapDistance) {
                this.object.position.x = maxSnapPoint;
                return true;
            }
        } else {
            if (this.object.position.z <= minSnapPoint + minMaxPositionSnapDistance) {
                this.object.position.z = minSnapPoint;
                return true;
            }
            if (this.object.position.z >= maxSnapPoint - minMaxPositionSnapDistance) {
                this.object.position.z = maxSnapPoint;
                return true;
            }
        }

        return false;
    }

    trySnapToInnerWall(wallOrientation, range) {
        let minSnapPoint = toFixedFloat(range.start);
        let maxSnapPoint = toFixedFloat(range.finish);

        if (wallOrientation === "x") {
            if (this.object.position.x <= minSnapPoint && this.object.position.x >= minSnapPoint - minMaxPositionSnapDistance) {
                this.object.position.x = minSnapPoint;
                return true;
            }
            if (this.object.position.x >= maxSnapPoint && this.object.position.x <= maxSnapPoint + minMaxPositionSnapDistance) {
                this.object.position.x = maxSnapPoint;
                return true;
            }
        } else {
            if (this.object.position.z <= minSnapPoint && this.object.position.z >= minSnapPoint - minMaxPositionSnapDistance) {
                this.object.position.z = minSnapPoint;
                return true;
            }
            if (this.object.position.z >= maxSnapPoint && this.object.position.z <= maxSnapPoint + minMaxPositionSnapDistance) {
                this.object.position.z = maxSnapPoint;
                return true;
            }
        }

        return false;
    }
}
